import React from "react";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "config/i18n";
import { Alert, Button, Form, FormField, PasswordInput, Radio, Select } from "../ui";
import type { UserData } from "../../types/state";

interface ProfileParametersFormProps extends WithTranslation {
  onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  formData: {
    allow_email?: boolean | number;
    allow_notification?: boolean | number;
    display_notification?: boolean | number;
    language?: string;
    password?: string;
    passwordConfirm?: string;
  };
  formError: { password?: { code: string; message: string }; [key: string]: unknown };
  userData: UserData;
  customerData?: unknown;
  message?: unknown;
  readonly?: boolean;
}

function renderRadioGroup(
  name: string,
  legend: React.ReactNode,
  value: boolean | number | undefined,
  onChange: ProfileParametersFormProps["onChange"],
  yesId: string,
  noId: string,
  t: WithTranslation["t"]
) {
  return (
    <fieldset className="ComposedForm-block">
      <legend className="ComposedForm-label">{legend}</legend>
      <Radio
        className="ComposedForm-input radio"
        name={name}
        id={yesId}
        onChange={onChange}
        value={1}
        defaultChecked={value == 1}
        label={t("common.label_yes")}
      />
      <Radio
        className="ComposedForm-input radio"
        name={name}
        id={noId}
        onChange={onChange}
        value={0}
        defaultChecked={value == 0}
        label={t("common.label_no")}
      />
    </fieldset>
  );
}

function ProfileParametersForm({
  t,
  formData,
  formError,
  onChange,
  onSubmit,
  userData,
  readonly,
}: ProfileParametersFormProps) {
  const { password, passwordConfirm } = formData;

  return (
    <Form action="/" onSubmit={onSubmit} className="ComposedForm" noValidate>
      <fieldset className="ComposedForm-fieldset">
        <legend className="ComposedForm-legend">
          {t("profile.parameters.label_notifications")}
        </legend>
        <div className="ComposedForm-fieldsetInner">
          <FormField
            className="ComposedForm-block"
            label={t("profile.parameters.label_default_language")}
            htmlFor="ComposedForm-language"
            hint={t("profile.parameters.info_default_language")}
          >
            <Select
              className="ComposedForm-input"
              name="language"
              id="ComposedForm-language"
              onChange={onChange}
              value={formData.language}
              options={SUPPORTED_LANGUAGES.map((language) => ({
                value: language,
                label: t("profile.parameters.language_" + language),
              }))}
            />
          </FormField>
          {renderRadioGroup(
            "allow_email",
            t("profile.parameters.label_allow_emails"),
            userData.allow_email as boolean | number | undefined,
            onChange,
            "ComposedForm-allow_email-yes",
            "ComposedForm-allow_email-no",
            t
          )}
        </div>
      </fieldset>

      {readonly && userData.must_change_password ? (
        <fieldset className="ComposedForm-fieldset">
          <legend className="ComposedForm-legend">
            {t("profile.parameters.label_authentication")}
          </legend>
          <div className="ComposedForm-fieldsetInner">
            <div className="ComposedForm-block">
              <label
                className="ComposedForm-label FlashMessage FlashMessage--error"
                htmlFor="ComposedForm-password"
              >
                {t("profile.parameters.label_must_change_password")}
              </label>
            </div>
          </div>
        </fieldset>
      ) : null}

      {!readonly ? (
        <fieldset className="ComposedForm-fieldset">
          <legend className="ComposedForm-legend">
            {t("profile.parameters.label_authentication")}
          </legend>
          <div className="ComposedForm-fieldsetInner">
            {userData.must_change_password ? (
              <Alert variant="error" className="ComposedForm-messageLine error">
                {t("profile.parameters.temporary_password_warning")}
              </Alert>
            ) : null}
            <FormField
              className="ComposedForm-block"
              label={t("profile.parameters.label_new_password")}
              htmlFor="ComposedForm-password"
              hint={t("profile.parameters.info_new_password")}
              error={formError.password ? formError.password.message : undefined}
            >
              <PasswordInput
                className="ComposedForm-input"
                name="password"
                id="ComposedForm-password"
                autoComplete="new-password"
                onChange={onChange}
                value={password}
              />
            </FormField>
            <FormField
              className="ComposedForm-block"
              label={t("profile.parameters.label_check_password")}
              htmlFor="ComposedForm-passwordConfirm"
            >
              <PasswordInput
                className="ComposedForm-input"
                name="passwordConfirm"
                id="ComposedForm-passwordConfirm"
                onChange={onChange}
                value={passwordConfirm}
              />
            </FormField>
          </div>
        </fieldset>
      ) : null}

      <div className="ComposedForm-block isActionLine">
        <Button type="submit" size="lg" className="Button ComposedForm-submit isLarge">
          {t("common.button_register")}
        </Button>
      </div>
    </Form>
  );
}

export default compose(withTranslation())(ProfileParametersForm);
